import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ThumbsUp, Lightbulb, ChevronDown, SmilePlus, BicepsFlexed } from "lucide-react";
import {
  NoHelpfulPracticesFound,
  NoPracticesFound,
} from "../../StudentData/components/AlertComponents";
import { getBgEmotionColor, getTextEmotionColor } from "../utils";
import {
  fetchDataFromFiles,
  randomInteger,
} from "../components/Recommendations";
import {
  getWellnessActionBackgroundColor,
  getWellnessActionDisplay,
  getWellnessActionImageSrc,
  isOtherWellnessAction,
} from "@/utils/wellnessActions";

const emotionEmojiImgMapping = [
  ["sad", "images/emotions/sad.png"],
  ["tired", "images/emotions/tired.png"],
  ["lonely", "images/emotions/lonely.png"],
  ["happy", "images/emotions/happy.png"],
  ["grateful", "images/emotions/grateful.png"],
  ["calm", "images/emotions/calm.png"],
  ["stressed", "images/emotions/stressed.png"],
  ["scared", "images/emotions/scared.png"],
  ["angry", "images/emotions/angry.png"],
  ["all", "images/emotions/all_emotions.png"],
];

const emotionColorMapping = {
  sad: "#C9E2FB",
  tired: "#C9E2FB",
  lonely: "#C9E2FB",
  happy: "#C8FADD",
  grateful: "#C8FADD",
  calm: "#C8FADD",
  stressed: "#D2BCE5",
  scared: "#D2BCE5",
  angry: "#D2BCE5",
  all: "#C8FADD",
};

const WhatHelps = ({
  studentData = [],
  selectedEmotion,
  selectedTimeframe,
  selectedEmotionColor,
  setSelectedEmotionColor,
  setSelectedEmotion,
  emotionPercents = {},
  adultActionImages,
}) => {
  const [selectedIdea, setSelectedIdea] = useState("");
  const selectIdea = async () => {
    let currentEmotion = "all";

    if (selectedEmotion == "all") {
      let filteredEmotions = emotionPercents.filter(
        (emotion) => emotion.count > 0
      );
      const randomIndex = randomInteger(0, filteredEmotions.length - 1);
      if (filteredEmotions.length > 0) {
        currentEmotion = filteredEmotions[randomIndex].name;
      } else {
        let allEmotions = [
          "sad",
          "tired",
          "lonely",
          "happy",
          "grateful",
          "calm",
          "stressed",
          "scared",
          "angry",
        ];
        const randomIndex = randomInteger(0, allEmotions.length - 1);
        currentEmotion = allEmotions[randomIndex];
      }
    } else {
      currentEmotion = selectedEmotion;
    }

    // Determine the file type based on selected emotion
    const fileType = "single";

    try {
      // fetch ideas from the assets/recommendations folder
      const ideas = await fetchDataFromFiles([
        "BoostAcademics",
        currentEmotion,
        "MyStoryIdeas",
        fileType,
      ]);

      if (ideas.status === "success") {
        if (ideas.data.considerations && ideas.data.considerations.length > 0) {
          const randomIndex = randomInteger(
            0,
            ideas.data.considerations.length - 1
          );
          setSelectedIdea(ideas.data.considerations[randomIndex]);
        }
      } else {
        console.error("Failed to fetch ideas:", ideas.message);
      }
    } catch (error) {
      console.error("Error fetching ideas:", error);
    }
  };

  // Load an idea when the component first mounts and when data changes
  useEffect(() => {
    selectIdea();
  }, [emotionPercents, studentData, selectedEmotion]); // Include selectedEmotion to ensure correct emotion is used
  const selectedEmotionPercent =
    selectedEmotion === "all"
      ? ""
      : emotionPercents.find((e) => e.name === selectedEmotion)
          ?.currentPercent || 0;
  const selectedEmotionLabel =
    selectedEmotion === "all" ? "all emotions" : selectedEmotion;
  const selectedEmotionImageSrc = (emotionEmojiImgMapping.find(
    ([name]) => name === selectedEmotion
  ) || [null, "images/emotions/all_emotions.png"])[1];
  const [mobileEmotionOpen, setMobileEmotionOpen] = useState(false);
  const mobileDropdownRef = useRef(null);

  useEffect(() => {
    if (!mobileEmotionOpen) return;
    const handleClickOutside = (event) => {
      if (
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target)
      ) {
        setMobileEmotionOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileEmotionOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileEmotionOpen]);

  const actionCounts = (studentData || []).reduce((acc, dataPoint) => {
    const action = isOtherWellnessAction(dataPoint.action)
      ? "Other"
      : getWellnessActionDisplay(dataPoint.action);
    const { helpful, emotion } = dataPoint;
    if (selectedEmotion !== "all" && emotion !== selectedEmotion) return acc;

    if (!acc[action]) {
      acc[action] = { total: 0, helpful: 0 };
    }

    acc[action].total++;
    if (helpful) acc[action].helpful++;

    return acc;
  }, {});
  const helpfulActions = Object.entries(actionCounts)
    .filter(([, counts]) => counts.helpful > 0)
    .map(([action, counts]) => {
      const percent = ((counts.helpful / counts.total) * 100).toFixed(1);
      return { action, count: counts.helpful, total: counts.total, percent };
    });

  const topThree = helpfulActions.sort((a, b) => b.count - a.count).slice(0, 3);

  const handleImageClick = (alt) => {
    setSelectedEmotion(alt);
    setSelectedEmotionColor(emotionColorMapping[alt]);
  };

  return (
    <div
      className="flex-1 bg-white shadow-md rounded p-3 sm:p-4 2xl:p-8"
      data-onboarding-id="emotions-helping"
    >
      <div className="flex items-center mb-2 gap-2 sm:mb-3">
        <BicepsFlexed className="w-5 h-5 text-600 shrink-0" />
        <h3 className="text-xl sm:text-2xl font-bold">
         What Helps Me
        </h3>
      </div>
      <div className="hidden sm:flex flex-wrap gap-4 justify-between py-4 bg-white">
        {emotionEmojiImgMapping.map(([alt, src], index) => (
          <div
            key={index}
            className={`flex flex-col gap-1 items-center cursor-pointer ${
              selectedEmotion === alt ? "opacity-100 sm:gap-6 sm:px-4" : "opacity-50"
            }`}
            onMouseEnter={() => {
              setSelectedEmotion(alt);
              setSelectedEmotionColor(emotionColorMapping[alt]);
            }}
          >
            <motion.img
              key={index}
              src={src}
              alt={alt}
              className="w-10 h-10"
              initial={{ scale: 1, rotate: 0, y: 0 }}
              animate={{
                scale: selectedEmotion === alt ? 2 : 1,
                rotate: 0,
                y: 0,
              }}
              whileHover={{
                scale: 2,
                rotate: [-10, 10, -10, 10, -10, 0],
                y: [0, -2, 2, -2, 0],
                transition: {
                  duration: 0.8,
                  repeat: Infinity,
                  repeatType: "loop",
                  ease: "easeInOut",
                },
              }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              style={{ backgroundColor: alt.color }}
            />
            <p className="capitalize text-[16px]">{alt}</p>
          </div>
        ))}
      </div>
      <div className="border border-[#DDDDDD] rounded p-2 sm:p-3 xl:min-h-80 m-auto flex justify-between flex-col">
        <div>
          <div className="mb-2">
            <div className="text-[18px] font-bold flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>When I feel</span>
              <span className="hidden sm:inline-flex items-center gap-2">
                <span className={`${getTextEmotionColor(selectedEmotion)}`}>
                  {selectedEmotionLabel},
                </span>
              </span>
              {/* mobile version emotion selector */}
              <span
                className="inline sm:hidden align-middle relative"
                ref={mobileDropdownRef}
              >
                <div
                  className="inline-flex items-center gap-1.5 cursor-pointer"
                  onClick={() => setMobileEmotionOpen(!mobileEmotionOpen)}
                >
                  {selectedEmotionImageSrc && (
                    <img
                      src={selectedEmotionImageSrc}
                      alt={selectedEmotionLabel}
                      className="inline-block w-auto h-5"
                      style={{
                        color: emotionColorMapping[selectedEmotion],
                      }}
                    />
                  )}
                  <span
                    className={`${getTextEmotionColor(selectedEmotion)}`}
                  >
                    {selectedEmotionLabel}
                  </span>
                  <ChevronDown className="inline-block w-4 h-4 text-black/60" />
                </div>
                {mobileEmotionOpen && (
                  <div className="absolute left-0 top-full mt-1 px-3 py-1 bg-white border border-[#d6d4cf] rounded shadow z-10 w-32">
                    {emotionEmojiImgMapping.map(([name, src]) => (
                      <div
                        key={name}
                        className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          handleImageClick(name);
                          setMobileEmotionOpen(false);
                        }}
                      >
                        <img
                          src={src}
                          alt={name}
                          className="w-auto h-5"
                          style={{ color: emotionColorMapping[name] }}
                        />
                        <span
                          className={`capitalize ${
                            name === selectedEmotion
                              ? getTextEmotionColor(name)
                              : "text-black"
                          }`}
                        >
                          {name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </span>
              <span>it helps me to:</span>
            </div>
          </div>
          {topThree.length === 0 && (
            <div className="flex justify-center items-center py-2 sm:py-3 w-100% xl:min-h-64">
              <NoHelpfulPracticesFound />
            </div>
          )}
          {topThree.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              {topThree.map(({ action, count, total, percent }) => {
                const isOther = isOtherWellnessAction(action);
                const displayAction = getWellnessActionDisplay(action);
                const imageSrc = getWellnessActionImageSrc(action, {
                  adultActionImages,
                });
                const backgroundColor = getWellnessActionBackgroundColor(action);

                return (
                  <div
                    key={action}
                    className="p-2 sm:p-3 font-bold flex flex-col items-center text-center border-b sm:border-b-0 border-[#DDDDDD]"
                  >
                    <div
                      className="flex items-center justify-center w-36 2xl:w-40 h-36 2xl:h-40 rounded-full shrink-0 mb-3"
                      style={{ backgroundColor }}
                    >
                      <img
                        src={imageSrc}
                        alt={displayAction}
                        className="w-28 2xl:w-36 h-24 2xl:h-30 object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="text-[16px] sm:min-h-[48px] font-medium flex items-start">
                      {displayAction}
                    </div>
                    <div className="sm:mt-2 flex items-center text-sm">
                      <span>{percent}% helpful</span>
                      <span className="text-[12px] opacity-50 ml-1">
                      ({count} out of {total} times)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div
          className="p-2 sm:p-3 gap-1 flex flex-row rounded"
          style={{ backgroundColor: selectedEmotionColor }}
          data-onboarding-id="emotions-idea"
        >
          <Lightbulb className="w-6 h-6 shrink-0" />
          IDEA:{" "}
          {selectedIdea ||
            "Remember that emotions come and go. You can do something small to release a little bit of stress (like stretch or tell yourself something nice)."}
        </div>
      </div>
    </div>
  );
};

export default WhatHelps;
